
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { teamMembers } from "@/data/teamData";
import { Linkedin, Instagram, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const TeamMemberDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const member = teamMembers.find((m) => m.id === id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (!member) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center bg-black text-white">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold mb-4">Member Not Found</h1>
                        <Button onClick={() => navigate("/who")} variant="outline">
                            Back to Team
                        </Button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Hero Section */}
            <section className="relative pt-48 pb-0 bg-purple text-white overflow-hidden min-h-[60vh] flex flex-col justify-end">
                <div className="container px-4 md:px-6 max-w-7xl mx-auto h-full relative z-10">
                    <div className="grid lg:grid-cols-12 gap-12 items-end h-full">

                        {/* Text Content */}
                        <div className="lg:col-span-7 pb-20 lg:pb-32">
                            {/* Create the 'ABOUT _____' line style */}
                            <div className="flex items-center gap-4 mb-12 opacity-80">
                                <span className="text-sm font-bold tracking-[0.2em]">About</span>
                            </div>

                            <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[0.9] mb-6">
                                {member.name}
                            </h1>
                            <p className="text-2xl md:text-3xl font-light opacity-90">
                                {member.role}
                            </p>
                        </div>

                        {/* Image */}
                        <div className="lg:col-span-5 relative h-full flex items-end lg:static">
                            {/* Back Button (Absolute Top Right of this section or page) */}
                            <div className="absolute -top-32 right-0 hidden lg:block z-20">
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate("/who")}
                                    className="text-white hover:bg-white/10 hover:text-black"
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to Team
                                </Button>
                            </div>

                            <div className="relative w-full aspect-[4/5] lg:absolute lg:right-0 lg:bottom-0 lg:w-auto lg:h-[600px] lg:max-w-[45vw] mt-8 lg:mt-0 z-0">
                                <img
                                    src={member.image}
                                    alt={member.name}
                                    className="w-full h-full object-contain object-bottom lg:object-right-bottom"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bio Content Section */}
            <section className="py-20 bg-white text-black min-h-[50vh]">
                <div className="container px-4 md:px-6 max-w-4xl mx-auto">
                    {/* Mobile Back Button */}
                    <div className="lg:hidden mb-8">
                        <Button
                            variant="ghost"
                            onClick={() => navigate("/who")}
                            className="hover:bg-black/5 -ml-4"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Team
                        </Button>
                    </div>

                    <div className="flex gap-6 mb-12">
                        {member.instagram && (
                            <a
                                href={member.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-3 bg-black/5 rounded-full hover:bg-[#c0ff09] hover:text-black transition-colors text-black"
                                aria-label="Instagram"
                            >
                                <Instagram className="w-5 h-5" />
                            </a>
                        )}
                        {member.linkedin && (
                            <a
                                href={member.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-3 bg-black/5 rounded-full hover:bg-[#c0ff09] hover:text-black transition-colors text-black"
                                aria-label="LinkedIn"
                            >
                                <Linkedin className="w-5 h-5" />
                            </a>
                        )}
                    </div>

                    <div
                        className="space-y-8 text-lg md:text-xl leading-relaxed font-normal !text-black text-black"
                        style={{ color: '#000000', opacity: 1 }}
                    >
                        {member.bioLong.map((paragraph, idx) => (
                            <p key={idx} className="text-black" style={{ color: '#000000' }}>{paragraph}</p>
                        ))}
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default TeamMemberDetail;
